import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiPlus, FiBriefcase, FiMapPin, FiUsers, FiSearch, FiFilter } from '../utils/icons';
import { getOrganizations } from '../services/adminService';
import { Organization } from '../types';
import { OrganizationModal } from '../components/organization/OrganizationModal';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 16px;
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PageTitle = styled.h1`
  font-size: 32px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.5px;
`;

const PageSubtitle = styled.p`
  font-size: 15px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
  font-weight: 400;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  }

  svg {
    font-size: 18px;
  }
`;

const FiltersBar = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 16px;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.divider};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
  }
`;

const SearchBox = styled.div`
  flex: 1;
  position: relative;
  max-width: 400px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    max-width: 100%;
    width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 12px 10px 40px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 6px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.background};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const SearchIcon = styled(FiSearch)`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 16px;
`;

const FilterSelect = styled.select`
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 6px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.background};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const StatCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  padding: 20px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const OrganizationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const OrgCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 8px;
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.colors.divider};
  transition: all 0.2s;
  cursor: pointer;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
  }
`;

const OrgHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const OrgName = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
  background: ${({ status }) =>
    status === 'active' ? '#10b98120' : status === 'inactive' ? '#6b728020' : '#f59e0b20'};
  color: ${({ status }) =>
    status === 'active' ? '#10b981' : status === 'inactive' ? '#6b7280' : '#f59e0b'};
`;

const OrgDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};

  svg {
    font-size: 16px;
    flex-shrink: 0;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 15px;
`;

const ErrorMessage = styled.div`
  padding: 16px 20px;
  background-color: ${({ theme }) => theme.colors.surfaceLight};
  color: ${({ theme }) => theme.colors.error};
  border-radius: 12px;
  border-left: 4px solid ${({ theme }) => theme.colors.error};
  font-size: 14px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${({ theme }) => theme.colors.text.secondary};

  h3 {
    font-size: 18px;
    margin: 0 0 8px 0;
    color: ${({ theme }) => theme.colors.text.primary};
  }

  p {
    font-size: 14px;
    margin: 0 0 20px 0;
  }
`;

export const OrganizationsPage: React.FC = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [filteredOrgs, setFilteredOrgs] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    loadOrganizations();
  }, []);

  useEffect(() => {
    filterOrganizations();
  }, [organizations, searchQuery, statusFilter]);

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getOrganizations(undefined, { page: 1, limit: 100 });

      if (response.success && response.data) {
        setOrganizations(response.data);
      } else {
        setError('Failed to load organizations');
      }
    } catch (err) {
      setError('An error occurred while loading organizations');
      console.error('Error loading organizations:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterOrganizations = () => {
    let filtered = organizations;

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((org) => org.status === statusFilter);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (org) =>
          org.name.toLowerCase().includes(query) ||
          org.email?.toLowerCase().includes(query) ||
          org.orgId?.toString().includes(query)
      );
    }

    setFilteredOrgs(filtered);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleOrganizationCreated = () => {
    loadOrganizations();
    setIsModalOpen(false);
  };

  const stats = {
    total: organizations.length,
    active: organizations.filter((org) => org.status === 'active').length,
    inactive: organizations.filter((org) => org.status === 'inactive').length,
    suspended: organizations.filter((org) => org.status === 'suspended').length,
  };

  if (loading) {
    return <LoadingMessage>Loading organizations...</LoadingMessage>;
  }

  return (
    <PageContainer>
      <PageHeader>
        <HeaderLeft>
          <PageTitle>Organizations</PageTitle>
          <PageSubtitle>Manage customer organizations and their details</PageSubtitle>
        </HeaderLeft>
        <AddButton onClick={() => setIsModalOpen(true)}>
          <FiPlus />
          Add Organization
        </AddButton>
      </PageHeader>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <StatsGrid>
        <StatCard>
          <StatLabel>Total Organizations</StatLabel>
          <StatValue>{stats.total}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>Active</StatLabel>
          <StatValue>{stats.active}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>Inactive</StatLabel>
          <StatValue>{stats.inactive}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>Suspended</StatLabel>
          <StatValue>{stats.suspended}</StatValue>
        </StatCard>
      </StatsGrid>

      <FiltersBar>
        <SearchBox>
          <SearchIcon />
          <SearchInput
            type="text"
            placeholder="Search organizations..."
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </SearchBox>
        <FilterSelect value={statusFilter} onChange={handleStatusFilterChange}>
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="suspended">Suspended</option>
        </FilterSelect>
      </FiltersBar>

      {filteredOrgs.length === 0 && !loading ? (
        <EmptyState>
          <h3>No organizations found</h3>
          <p>
            {searchQuery || statusFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'Get started by adding your first organization'}
          </p>
          {!searchQuery && statusFilter === 'all' && (
            <AddButton onClick={() => setIsModalOpen(true)}>
              <FiPlus />
              Add Organization
            </AddButton>
          )}
        </EmptyState>
      ) : (
        <OrganizationsGrid>
          {filteredOrgs.map((org) => (
            <OrgCard key={org.orgId}>
              <OrgHeader>
                <OrgName>{org.name}</OrgName>
                <StatusBadge status={org.status || 'active'}>{org.status || 'active'}</StatusBadge>
              </OrgHeader>
              <OrgDetails>
                <DetailRow>
                  <FiBriefcase />
                  ID: {org.orgId}
                </DetailRow>
                {org.email && (
                  <DetailRow>
                    📧 {org.email}
                  </DetailRow>
                )}
                {org.phone && (
                  <DetailRow>
                    📞 {org.phone}
                  </DetailRow>
                )}
                {org.locationsCount !== undefined && (
                  <DetailRow>
                    <FiMapPin />
                    {org.locationsCount} Locations
                  </DetailRow>
                )}
                {org.usersCount !== undefined && (
                  <DetailRow>
                    <FiUsers />
                    {org.usersCount} Users
                  </DetailRow>
                )}
              </OrgDetails>
            </OrgCard>
          ))}
        </OrganizationsGrid>
      )}

      <OrganizationModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSuccess={handleOrganizationCreated}
      />
    </PageContainer>
  );
};
